(() => {
  const CONFIG = window.AOS_COOL_SHIPPING || {};

  const COOL_TAG = CONFIG.coolTag || 'cool_required';
  const FEE_VARIANT_ID = Number(CONFIG.feeVariantId);
  const FEE_PRODUCT_HANDLE = CONFIG.feeProductHandle || 'cool-shipping-fee';
  const FEE_PRODUCT_TITLE = CONFIG.feeProductTitle || 'クール便';
  const ROOT_URL = CONFIG.rootUrl || (window.Shopify?.routes?.root || '/');

  if (!FEE_VARIANT_ID) {
    console.warn('[AOS Cool Shipping] feeVariantId が設定されていません。');
    return;
  }

  const root = ROOT_URL.endsWith('/') ? ROOT_URL : `${ROOT_URL}/`;
  const url = (path) => `${root}${path.replace(/^\//, '')}`;

  const nativeFetch = window.fetch.bind(window);
  const tagCache = new Map();

  let syncTimer = null;
  let syncing = false;
  let queued = false;
  let markTimer = null;
  let observerStarted = false;
  let cartPageReloadRequested = false;

  async function shopifyFetch(path, options = {}) {
    const response = await nativeFetch(url(path), {
      credentials: 'same-origin',
      ...options
    });

    if (!response.ok) {
      const text = await response.text();
      throw new Error(`[AOS Cool Shipping] ${path} failed: ${response.status} ${text}`);
    }

    return response.json();
  }

  async function getCart() {
    return shopifyFetch('cart.js');
  }

  async function getProductHasCoolTag(handle) {
    if (!handle) return false;

    if (tagCache.has(handle)) {
      return tagCache.get(handle);
    }

    const promise = shopifyFetch(`products/${encodeURIComponent(handle)}.js`)
      .then((product) => {
        const tags = Array.isArray(product.tags) ? product.tags : [];
        return tags.includes(COOL_TAG);
      })
      .catch((error) => {
        console.warn(`[AOS Cool Shipping] 商品タグの取得に失敗しました: ${handle}`, error);
        return false;
      });

    tagCache.set(handle, promise);
    return promise;
  }

  function getFeeItems(cart) {
    return cart.items.filter((item) => Number(item.variant_id) === FEE_VARIANT_ID);
  }

  async function cartHasCoolRequiredItem(cart) {
    for (const item of cart.items) {
      if (Number(item.variant_id) === FEE_VARIANT_ID) continue;

      const hasCoolTag = await getProductHasCoolTag(item.handle);
      if (hasCoolTag) return true;
    }

    return false;
  }

  async function addFeeItem() {
    return shopifyFetch('cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        items: [
          {
            id: FEE_VARIANT_ID,
            quantity: 1,
            properties: {
              _auto_cool_shipping_fee: 'true'
            }
          }
        ]
      })
    });
  }

  async function changeLineItemQuantity(itemKey, quantity) {
    return shopifyFetch('cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        id: itemKey,
        quantity
      })
    });
  }

  function isCartPage() {
    const pathname = window.location.pathname.replace(/\/$/, '') || '/';

    let rootPath = '/';

    try {
      rootPath = new URL(root, window.location.origin).pathname;
    } catch (error) {
      rootPath = root;
    }

    rootPath = rootPath.replace(/\/$/, '');

    const cartPath = `${rootPath}/cart`
      .replace(/\/+/g, '/')
      .replace(/\/$/, '');

    return pathname === cartPath || pathname.endsWith('/cart');
  }

  function reloadCartPageSoon() {
    if (!isCartPage()) return;
    if (cartPageReloadRequested) return;

    cartPageReloadRequested = true;

    window.setTimeout(() => {
      window.location.reload();
    }, 450);
  }

  function injectCoolFeeProtectionStyle() {
    if (document.getElementById('aos-cool-fee-protection-style')) return;

    const style = document.createElement('style');
    style.id = 'aos-cool-fee-protection-style';

    style.textContent = `
      .aos-cool-fee-line .quantity-selector,
      .aos-cool-fee-line quantity-selector,
      .aos-cool-fee-line line-item-quantity,
      .aos-cool-fee-line [data-quantity-selector],
      .aos-cool-fee-line [data-line-item-quantity],
      .aos-cool-fee-line input[name="updates[]"],
      .aos-cool-fee-line input[type="number"],
      .aos-cool-fee-line button[name="minus"],
      .aos-cool-fee-line button[name="plus"],
      .aos-cool-fee-line button[aria-label*="減らす"],
      .aos-cool-fee-line button[aria-label*="増やす"],
      .aos-cool-fee-line button[aria-label*="削除"],
      .aos-cool-fee-line a[href*="/cart/change"],
      .aos-cool-fee-line a[href*="quantity=0"],
      .aos-cool-fee-line [data-cart-remove],
      .aos-cool-fee-line [data-line-item-action],
      .aos-cool-fee-line .line-item__remove,
      .aos-cool-fee-line .line-item__remove-button,
      .aos-cool-fee-line .cart-item__remove,
      .aos-cool-fee-line cart-remove-button {
        display: none !important;
        pointer-events: none !important;
      }

      .aos-cool-fee-note {
        margin-top: 8px;
        font-size: 12px;
        line-height: 1.5;
        color: #666;
      }
    `;

    document.head.appendChild(style);
  }

  function getPossibleCartLineContainers() {
    return Array.from(
      document.querySelectorAll(
        [
          'tr',
          'line-item',
          '.line-item',
          '.cart-item',
          '.cart__item',
          '[data-line-item]',
          '[data-cart-item]'
        ].join(',')
      )
    );
  }

  function findCoolFeeLines() {
    const lines = new Set();

    const productLinks = Array.from(
      document.querySelectorAll(`a[href*="/products/${FEE_PRODUCT_HANDLE}"]`)
    );

    productLinks.forEach((link) => {
      const line = link.closest(
        'tr, line-item, .line-item, .cart-item, .cart__item, [data-line-item], [data-cart-item]'
      );

      if (line) {
        lines.add(line);
      }
    });

    if (FEE_PRODUCT_TITLE) {
      getPossibleCartLineContainers().forEach((line) => {
        const text = line.textContent || '';

        if (text.includes(FEE_PRODUCT_TITLE) && text.includes('660')) {
          lines.add(line);
        }
      });
    }

    return Array.from(lines);
  }

  function addCoolFeeNote(line) {
    if (line.querySelector('.aos-cool-fee-note')) return;

    const links = Array.from(
      line.querySelectorAll(`a[href*="/products/${FEE_PRODUCT_HANDLE}"]`)
    );

    const titleLink =
      links.find((link) => link.textContent.trim().length > 0) ||
      links[0] ||
      null;

    const note = document.createElement('div');
    note.className = 'aos-cool-fee-note';
    note.textContent = 'クール便対象商品が含まれるため、自動で追加されています。■対象商品/ワイン類・オリーブオイル類・バルサミコ酢・ワインビネガー類・ツナのオイル漬け・アンチョビフィレ・コラトゥーラ';

    if (titleLink && titleLink.parentElement) {
      titleLink.parentElement.appendChild(note);
      return;
    }

    const fallbackTarget =
      line.querySelector('.line-item__info') ||
      line.querySelector('.cart-item__details') ||
      line.querySelector('td') ||
      line;

    fallbackTarget.appendChild(note);
  }

  function markCoolFeeLine() {
    injectCoolFeeProtectionStyle();

    const lines = findCoolFeeLines();

    lines.forEach((line) => {
      line.classList.add('aos-cool-fee-line');
      addCoolFeeNote(line);
    });
  }

  function scheduleMarkCoolFeeLine(delay = 100) {
    window.clearTimeout(markTimer);
    markTimer = window.setTimeout(markCoolFeeLine, delay);
  }

  function refreshPrestigeCart() {
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:refresh', {
        bubbles: true
      })
    );

    window.setTimeout(markCoolFeeLine, 200);
    window.setTimeout(markCoolFeeLine, 700);

    reloadCartPageSoon();
  }

  async function syncCoolShippingFee() {
    if (syncing) {
      queued = true;
      return;
    }

    syncing = true;

    try {
      const cart = await getCart();

      const hasCoolRequiredItem = await cartHasCoolRequiredItem(cart);
      const feeItems = getFeeItems(cart);

      let changed = false;

      if (hasCoolRequiredItem) {
        if (feeItems.length === 0) {
          await addFeeItem();
          changed = true;
        } else {
          const firstFeeItem = feeItems[0];

          if (firstFeeItem.quantity !== 1) {
            await changeLineItemQuantity(firstFeeItem.key, 1);
            changed = true;
          }

          const duplicateFeeItems = feeItems.slice(1);
          for (const duplicate of duplicateFeeItems) {
            await changeLineItemQuantity(duplicate.key, 0);
            changed = true;
          }
        }
      } else {
        for (const feeItem of feeItems) {
          await changeLineItemQuantity(feeItem.key, 0);
          changed = true;
        }
      }

      if (changed) {
        refreshPrestigeCart();
      }

      scheduleMarkCoolFeeLine(200);
    } catch (error) {
      console.error(error);
    } finally {
      syncing = false;

      if (queued) {
        queued = false;
        scheduleSync(200);
      }
    }
  }

  function scheduleSync(delay = 300) {
    window.clearTimeout(syncTimer);
    syncTimer = window.setTimeout(syncCoolShippingFee, delay);
  }

  function startCartDomObserver() {
    if (observerStarted || !document.body) return;

    observerStarted = true;

    const observer = new MutationObserver(() => {
      scheduleMarkCoolFeeLine(150);
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  document.addEventListener(
    'click',
    (event) => {
      const feeLine = event.target.closest('.aos-cool-fee-line');
      if (!feeLine) return;

      const blockedAction = event.target.closest(
        [
          'a[href*="/cart/change"]',
          'a[href*="quantity=0"]',
          'button',
          'input[name="updates[]"]',
          'input[type="number"]',
          '[data-cart-remove]',
          '[data-line-item-action]',
          '.line-item__remove',
          '.line-item__remove-button',
          '.cart-item__remove',
          'cart-remove-button',
          'quantity-selector',
          'line-item-quantity'
        ].join(',')
      );

      if (!blockedAction) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      scheduleSync(100);
    },
    true
  );

  document.addEventListener('variant:add', () => {
    scheduleSync(200);
    scheduleMarkCoolFeeLine(400);
  });

  document.addEventListener('line-item:change', () => {
    scheduleSync(300);
    window.setTimeout(() => scheduleSync(100), 1000);
    window.setTimeout(() => scheduleSync(100), 2000);
    scheduleMarkCoolFeeLine(400);
  });

  document.addEventListener('cart:change', () => {
    scheduleSync(300);
    window.setTimeout(() => scheduleSync(100), 1000);
    window.setTimeout(() => scheduleSync(100), 2000);
    scheduleMarkCoolFeeLine(400);
  });

  document.addEventListener('DOMContentLoaded', () => {
    injectCoolFeeProtectionStyle();
    markCoolFeeLine();
    startCartDomObserver();
    scheduleSync(300);
  });

  window.addEventListener('pageshow', () => {
    injectCoolFeeProtectionStyle();
    markCoolFeeLine();
    startCartDomObserver();
    scheduleSync(300);
  });

  window.fetch = async (...args) => {
    const response = await nativeFetch(...args);

    try {
      const requestUrl = typeof args[0] === 'string' ? args[0] : args[0]?.url;

      if (
        requestUrl &&
        /\/cart\/(add|change|update|clear)\.js/.test(requestUrl) &&
        !syncing
      ) {
        scheduleSync(300);
        window.setTimeout(() => scheduleSync(100), 1000);
        scheduleMarkCoolFeeLine(500);
      }
    } catch (error) {
      // ここでは何もしない
    }

    return response;
  };

  document.addEventListener(
    'click',
    async (event) => {
      const checkoutButton = event.target.closest(
        'button[name="checkout"], input[name="checkout"], a[href$="/checkout"], a[href*="/checkout?"]'
      );

      if (!checkoutButton) return;

      event.preventDefault();
      event.stopPropagation();

      checkoutButton.setAttribute('aria-busy', 'true');

      if ('disabled' in checkoutButton) {
        checkoutButton.disabled = true;
      }

      await syncCoolShippingFee();

      window.location.href = url('checkout');
    },
    true
  );
})();